import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import { APKEditableForm } from './APKEditableForm'
import { updateAppApk, getAppApk } from 'app/services/client'
import {
    makeTokenHeaderConfig,
    useTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { isTokenExpired, onInputChange, onSelectChange } from 'app/utils/hooks'
import { RootState } from 'app/state/types'
import { setApk, updateApk, setIsLoading } from 'app/state/appSlice'
import { handleServerAPIError } from 'app/services/utils'

const APKEditView = () => {
    const { apk } = useSelector((state: RootState) => state.appSlice)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { appid, id } = useParams()
    const [token] = useTokenHeaderConfig()

    const onApkChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onInputChange(e)(dispatch, updateApk)
        },
        [updateApk, onInputChange, dispatch],
    )

    const onApkSelectChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            onSelectChange(e)(dispatch, updateApk)
        },
        [updateApk, onSelectChange, dispatch],
    )

    const onApkArcChange = useCallback(
        (fieldName: string, value: number) => {
            dispatch(
                updateApk({
                    [fieldName]: value === 1,
                }),
            )
        },
        [dispatch, updateApk],
    )

    const onFormSubmit = useCallback(() => {
        if (appid && id) {
            dispatch(setIsLoading(true))
            updateAppApk(appid, apk, makeTokenHeaderConfig(token))
                .then(() => {
                    navigate(
                        generatePath(APP_ROUTES.APP_DETAILS, {
                            id: appid,
                        }),
                    )
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
                .finally(() => dispatch(setIsLoading(false)))
        }
    }, [
        dispatch,
        navigate,
        generatePath,
        appid,
        id,
        apk,
        updateAppApk,
        setIsLoading,
        handleServerAPIError,
    ])

    useLayoutEffect(() => {
        if (appid && id) {
            dispatch(setIsLoading(true))
            getAppApk(appid, id)
                .then(res => {
                    dispatch(
                        setApk(
                            Object.assign({
                                ...res.data,
                                is32bit: !res.data.is64bit,
                            }),
                        ),
                    )
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
                .finally(() => dispatch(setIsLoading(false)))
        }
    }, [])

    useEffect(() => {
        if (isTokenExpired()) navigate(APP_ROUTES.LOGIN)
    }, [])

    return (
        <APKEditableForm
            apk={apk}
            onApkChange={onApkChange}
            onApkSelectChange={onApkSelectChange}
            onSubmit={onFormSubmit}
            onApkArcChange={onApkArcChange}
            isEdit
        />
    )
}

export default APKEditView
